import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
  ChangeDetectorRef
} from '@angular/core';
import { CommonConfirmationModalData } from 'src/app/components/common/common-confirmation-modal/common-confirmation-modal.component.model';
import { SecurityService, WorkflowContextService } from 'src/app/services';

/** UI-ready grouped roles */
interface RoleGroup {
  clientId: string | null;
  county: string;
  roles: { id: string; name: string }[];
}
type ConfirmAction =
  | 'reset-password'
  | 'save-user'
  | 'role-removed'
  | 'role-added'
  | null;

@Component({
  selector: 'wm-user-settings-janus',
  standalone: false,
  templateUrl: './user-settings-janus.component.html',
  styleUrls: ['./user-settings-janus.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class UserSettingsJanusComponent implements OnInit, OnChanges {

  /* =============================
   * INPUTS
   * ============================= */
  @Input() row: any;
  @Input() user: any;
  @Input() closeExpansion!: () => void;
  // Added by amisha on 2026-04-30
  // Reason: allow the inline settings panel to push refreshed full-user data back into
  // the visible Janus list row after save/enable/disable actions.
  @Input() onUserUpdated?: (user: any) => void;

  /* =============================
   * STATE
   * ============================= */
  confirmAction: ConfirmAction = null;

  rolesByCounty: RoleGroup[] = [];
  userRoles: any[] = [];

  roles: any[] = [];
  filteredRoles: any[] = [];
  roleSearch = '';
  selectedRole: any = null;

  isRolesLoading = false;
  // Added by amisha on 2026-04-30
  // Reason: prevent duplicate actions while the full user payload is being fetched or refreshed
  // for the inline settings panel.
  isUserLoading = false;
  isAddRoleOpen = false;

  confirmationModal: CommonConfirmationModalData = this.getResetModal();
  resetPasswordText = 'RESET PASSWORD';
  canResetPassword = true;

  constructor(
    private securitySvc: SecurityService,
    private context: WorkflowContextService,
    private cdr: ChangeDetectorRef 
  ) { }

  /* =============================
   * INIT
   * ============================= */
  ngOnInit(): void {
    this.loadRoles();

    if (this.row?.id) {
      this.fetchFullUser(this.row.id);
      this.fetchUserRoles(this.row.id);
    }
  }

  /* =============================
   * CHANGES
   * ============================= */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['row']?.currentValue) {
      this.fetchFullUser(this.row.id);
      this.fetchUserRoles(this.row.id);
    }
  }

  /* =============================
   * MODAL HELPERS
   * ============================= */
  private getResetModal(): CommonConfirmationModalData {
    return {
      open: false,
      modalDescp: '',
      modalSubDescp: '',
      yesNoBtn: false,
      okBtn: false,
      closeBtn: false,
      status: 'default',
    };
  }

  private syncParentRow(): void {
    if (this.user && this.onUserUpdated) {
      this.onUserUpdated(this.user);
    }
  }

  closeModal(): void {
    this.confirmationModal = this.getResetModal();
  }

  /* =============================
   * API CALLS
   * ============================= */
  private fetchFullUser(userId: string): void {
    // Added by amisha on 2026-04-30
    // Reason: always work with the full user payload inside the inline settings panel so
    // save/enable/disable calls do not rely on the lighter search-result row object.
    this.isUserLoading = true;

    this.securitySvc.getUser(userId).subscribe({
      next: response => {
        if (!response) {
          this.isUserLoading = false;
          return;
        }

        this.user = { ...response };

        // Ensure the address object exists
        if (!this.user.address) {
          this.user.address = {
            name: 'Default',
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip: '',
            parentType: 'User'
          };
        }
        if (!this.user.phoneNumber) {
          this.user.phoneNumber = '';
        }
        if (!this.user.emailAddress) {
          this.user.emailAddress = '';
        }

        this.syncParentRow();
        this.isUserLoading = false;
        this.cdr.detectChanges(); 
      },
      error: () => {
        this.isUserLoading = false;
      }
    });
  }

  private fetchUserRoles(userId: string): void {
    this.securitySvc.getUserRoles(userId).subscribe({
      next: roles => {
        this.userRoles = roles;
        this.buildRolesFromUserRoles();
      },
      error: () => {
        this.rolesByCounty = [];
      },
    });
  }

  loadRoles(): void {
    this.isRolesLoading = true;

    this.securitySvc.getRoles(this.context.client.id).subscribe({
      next: roles => {
        this.roles = roles;
        this.isRolesLoading = false;
      },
      error: () => {
        this.isRolesLoading = false;
      },
    });
  }

  /* =============================
   * ROLE GROUPING (CORRECT SOURCE)
   * ============================= */
  private buildRolesFromUserRoles(): void {
    if (!this.userRoles.length) {
      this.rolesByCounty = [];
      return;
    }

    const grouped = new Map<string | null, RoleGroup>();

    this.userRoles.forEach(role => {
      const clientId = role.client?.id ?? null;
      const county = role.client?.name ?? 'Global';

      if (!grouped.has(clientId)) {
        grouped.set(clientId, {
          clientId,
          county,
          roles: [],
        });
      }

      grouped.get(clientId)!.roles.push({
        id: role.id,
        name: role.name,
      });
    });

    this.rolesByCounty = Array.from(grouped.values());
  }

  onConfirmOk(): void {
    const action = this.confirmAction;
    this.confirmAction = null;

    this.closeModal();

    switch (action) {
      case 'role-added':
      case 'role-removed':
        if (this.user?.id) this.fetchUserRoles(this.user.id);
        break;

      case 'save-user':
        // ✅ Closes the inline panel and returns to the search context without reloading the page
        this.closeExpansion();
        break;

      case 'reset-password':
        this.resetPasswordText = 'Email sent to user';
        this.canResetPassword = false;
        break;

      default:
        break;
    }
  }

  /* =============================
   * SAFE INPUT HANDLER
   * ============================= */
  onAddressChange(field: string, value: string): void {
    if (!this.user) {
      this.user = {};
    }
    
    if (!this.user.address) {
      this.user.address = {
        name: 'Default',
        parentType: 'User'
      };
    }
    
    this.user.address[field] = value;
  }

  /* =============================
   * ADD ROLE
   * ============================= */
  onRoleSearch(): void {
    const q = this.roleSearch.toLowerCase();
    this.filteredRoles = this.roles.filter(r =>
      r.name.toLowerCase().includes(q)
    );
  }

  selectRole(role: any): void {
    this.selectedRole = role;
    this.roleSearch = role.name;
    this.filteredRoles = [];
  }

  openAddRole(): void {
    this.isAddRoleOpen = true;
    this.roleSearch = '';
    this.filteredRoles = [];
    this.selectedRole = null;
  }

  cancelAddRole(): void {
    this.isAddRoleOpen = false;
    this.roleSearch = '';
    this.filteredRoles = [];
    this.selectedRole = null;
  }

  addSelectedRole(): void {
    if (!this.selectedRole || !this.user || this.isUserLoading) {
      return;
    }

    this.securitySvc.associateRole(this.user, this.selectedRole).subscribe({
      next: () => {
        this.confirmAction = 'role-added';

        this.confirmationModal = {
          ...this.getResetModal(),
          open: true,
          modalDescp: 'Role Added',
          modalSubDescp: 'The role has been added successfully.',
          okBtn: true,
          status: 'success',
        };

        this.cancelAddRole();
      },
      error: () => {
        this.confirmationModal = {
          ...this.getResetModal(),
          open: true,
          modalDescp: 'Add Role Failed',
          modalSubDescp: 'Unable to add role.',
          okBtn: true,
          status: 'error',
        };
      },
    });
  }

  /* =============================
   * REMOVE ROLE
   * ============================= */
  removeRole(roleId: string): void {
    // Added by amisha on 2026-04-30
    // Reason: block role removal until the full inline user state is ready to avoid
    // inconsistent API calls from partial data.
    if (!this.user?.id || this.isUserLoading) {
      return;
    }

    this.securitySvc.removeUserRole(this.user.id, roleId).subscribe({
      next: () => {
        this.confirmAction = 'role-removed';

        this.confirmationModal = {
          ...this.getResetModal(),
          open: true,
          modalDescp: 'Role Removed',
          modalSubDescp: 'The role has been removed successfully.',
          okBtn: true,
          status: 'success',
        };
      },
      error: () => {
        this.confirmationModal = {
          ...this.getResetModal(),
          open: true,
          modalDescp: 'Remove Failed',
          modalSubDescp: 'Unable to remove role.',
          okBtn: true,
          status: 'error',
        };
      },
    });
  }

  /* =============================
   * RESET PASSWORD
   * ============================= */
  openResetPasswordConfirm(): void {
    // Added by amisha on 2026-04-30
    // Reason: keep reset-password actions disabled while the inline user record is still loading.
    if (!this.user?.userName || !this.canResetPassword || this.isUserLoading) {
      return;
    }

    this.confirmAction = 'reset-password';

    this.confirmationModal = {
      ...this.getResetModal(),
      open: true,
      modalDescp: 'Reset Password',
      modalSubDescp: `Click ‘Yes’ to receive a reset link on your registered email ID: ${this.user.emailAddress}?`,
      yesNoBtn: true,
      status: 'info',
    };
  }

  onConfirmResetPassword(): void {
    this.closeModal();

    this.securitySvc.resetPassword(this.user.userName).subscribe({
      next: () => {
        this.confirmAction = 'reset-password';

        this.confirmationModal = {
          ...this.getResetModal(),
          open: true,
          modalDescp: 'Reset Password',
          modalSubDescp: 'Password reset email sent successfully.',
          okBtn: true,
          status: 'success',
        };

      },
      error: (err) => {
        console.error('[UserSettings] resetPassword ERROR:', err);
      }
    });
  }

  onResetPasswordOk(): void {
    this.resetPasswordText = 'Email sent to user';
    this.canResetPassword = false;
    this.closeModal();
  }

  /* =============================
   * SAVE USER
   * ============================= */
  openSaveConfirm(): void {
    // Added by amisha on 2026-04-30
    // Reason: allow save confirmation only after the inline panel has the fetched full user object.
    if (!this.user?.id || this.isUserLoading) {
      return;
    }

    this.confirmAction = 'save-user';

    this.confirmationModal = {
      ...this.getResetModal(),
      open: true,
      modalDescp: 'Save User',
      modalSubDescp: 'Are you sure you want to save the user details?',
      yesNoBtn: true,
      status: 'info',
    };
  }

  onConfirmSave(): void {
    this.closeModal();

    this.securitySvc.saveUser(this.user).subscribe({
      next: res => {
        // Added by amisha on 2026-04-30
        // Reason: immediately reflect saved user values in the list row behind the inline panel.
        this.syncParentRow();
        this.confirmAction = 'save-user';
        
        this.confirmationModal = {
          ...this.getResetModal(),
          open: true,
          modalDescp: res?.responseMessage ? 'Save Failed' : 'User Saved',
          modalSubDescp: res?.responseMessage || 'User saved successfully.',
          okBtn: true,
          status: res?.responseMessage ? 'error' : 'success',
        };
      },
    });
  }

  /* =============================
   * ENABLE / DISABLE
   * ============================= */
  enableUser(): void {
    // Added by amisha on 2026-04-30
    // Reason: prevent enable requests from firing against partial inline user data.
    if (!this.user || this.isUserLoading) {
      return;
    }

    this.securitySvc.enableUser(this.user).subscribe(() => {
      // Added by amisha on 2026-04-30
      // Reason: keep the visible list row status aligned with the refreshed inline user state.
      this.syncParentRow();
      this.fetchFullUser(this.user.id);
    });
  }

  disableUser(): void {
    // Added by amisha on 2026-04-30
    // Reason: prevent disable requests from firing while the inline user payload is still loading.
    if (!this.user || this.isUserLoading) {
      return;
    }

    this.securitySvc.disableUser(this.user).subscribe(() => {
      // Added by amisha on 2026-04-30
      // Reason: keep the visible list row status aligned with the refreshed inline user state.
      this.syncParentRow();
      this.fetchFullUser(this.user.id);
    });
  }
  
  onConfirmYes(): void {
    if (this.confirmAction === 'reset-password') {
      this.onConfirmResetPassword();
    }

    if (this.confirmAction === 'save-user') {
      this.onConfirmSave();
    }

    this.confirmAction = null;
  }

  /* =============================
   * CLOSE PANEL
   * ============================= */
  onClose(): void {
    this.closeExpansion();
  }
}
